import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";

const SORTS = [
  { v: "featured", l: "Featured" },
  { v: "bestselling", l: "Best Selling" },
  { v: "newest", l: "Newest" },
  { v: "price_asc", l: "Price: Low to High" },
  { v: "price_desc", l: "Price: High to Low" },
  { v: "rating", l: "Highest Rated" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ brands: [], genders: [], families: [], subcategories: [], sizes: [], min_price: 0, max_price: 500 });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const category = params.get("category") || "";
  const subcategory = params.get("subcategory") || "";
  const brand = params.get("brand") || "";
  const gender = params.get("gender") || "";
  const family = params.get("fragrance_family") || "";
  const size = params.get("size") || "";
  const intensity = params.get("intensity") || "";
  const inStock = params.get("in_stock") || "";
  const minRating = params.get("min_rating") || "";
  const maxPrice = params.get("max_price") || "";
  const sort = params.get("sort") || "featured";
  const best = params.get("best");
  const newA = params.get("new");
  const search = params.get("q") || "";

  useEffect(() => { api.get("/meta/filters").then((r) => setMeta(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const p = { sort };
    if (category) p.category = category;
    if (subcategory) p.subcategory = subcategory;
    if (brand) p.brand = brand;
    if (gender) p.gender = gender;
    if (family) p.fragrance_family = family;
    if (size) p.size = size;
    if (intensity) p.intensity = intensity;
    if (inStock) p.in_stock = true;
    if (minRating) p.min_rating = minRating;
    if (maxPrice) p.max_price = maxPrice;
    if (best) p.best_seller = true;
    if (newA) p.new_arrival = true;
    if (search) p.search = search;
    api.get("/products", { params: p }).then((r) => { setProducts(r.data); setLoading(false); });
  }, [category, subcategory, brand, gender, family, size, intensity, inStock, minRating, maxPrice, sort, best, newA, search]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const clearAll = () => {
    const next = new URLSearchParams();
    if (category) next.set("category", category);
    setParams(next);
  };

  const title = useMemo(() => {
    if (best) return "Best Sellers";
    if (newA) return "New Arrivals";
    if (subcategory) return subcategory;
    if (category) return category;
    if (search) return `Results for "${search}"`;
    return "The Collection";
  }, [best, newA, category, subcategory, search]);

  const isDefaultView = !category && !subcategory && !brand && !gender && !family && !size && !intensity && !inStock && !minRating && !maxPrice && !best && !newA && !search;

  const groups = useMemo(() => {
    const g = [
      { key: "Perfumes", label: "Perfumes", desc: "Signature eau de parfum", items: [] },
      { key: "Air Fresheners", label: "Air Fresheners", desc: "300ml home mists", items: [] },
      { key: "Candles", label: "Candles", desc: "Hand-poured luxury candles", items: [] },
      { key: "Skincare", label: "Skincare", desc: "Serums & body oils", items: [] },
      { key: "Soaps", label: "Soaps", desc: "Handmade herbal bars", items: [] },
    ];
    products.forEach((p) => {
      if (p.category === "Perfumes") g[0].items.push(p);
      else if (p.subcategory === "Air Fresheners") g[1].items.push(p);
      else if (p.subcategory === "Candles") g[2].items.push(p);
      else if (p.category === "Skincare") g[3].items.push(p);
      else if (p.category === "Soaps") g[4].items.push(p);
    });
    return g.filter((x) => x.items.length);
  }, [products]);

  const activeCount = [subcategory, brand, gender, family, size, intensity, inStock, minRating, maxPrice].filter(Boolean).length;

  const Group = ({ label, options, param, value }) => (
    <div>
      <p className="eyebrow mb-3">{label}</p>
      <div className="space-y-2">
        {options.map((o) => {
          const val = typeof o === "object" ? o.v : o;
          const lbl = typeof o === "object" ? o.l : o;
          return (
            <button key={val} data-testid={`filter-${param}-${String(val).replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setParam(param, value === String(val) ? "" : String(val))}
              className={`block text-sm transition-colors ${value === String(val) ? "text-[#D4AF37]" : "text-[#FAFAF7]/60 hover:text-[#FAFAF7]"}`}>
              {lbl}
            </button>
          );
        })}
      </div>
    </div>
  );

  const FilterPanel = () => (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <p className="eyebrow">Filters {activeCount > 0 && `· ${activeCount}`}</p>
        {activeCount > 0 && <button data-testid="clear-filters" onClick={clearAll} className="text-xs text-[#D4AF37] hover:underline">Clear all</button>}
      </div>
      <Group label="Category" param="category" value={category} options={["Perfumes", "Home Fragrance", "Skincare", "Soaps"]} />
      <Group label="Product Type" param="subcategory" value={subcategory} options={meta.subcategories} />
      <Group label="Brand" param="brand" value={brand} options={meta.brands} />
      <Group label="Gender" param="gender" value={gender} options={meta.genders} />
      <Group label="Fragrance Family" param="fragrance_family" value={family} options={meta.families} />
      <Group label="Size" param="size" value={size} options={meta.sizes} />
      <Group label="Intensity" param="intensity" value={intensity} options={["Subtle", "Moderate", "Intense"]} />
      <div>
        <p className="eyebrow mb-3">Max Price · ${maxPrice || meta.max_price}</p>
        <input type="range" data-testid="filter-price-range" min={meta.min_price} max={meta.max_price} step={5}
          value={maxPrice || meta.max_price} onChange={(e) => setParam("max_price", e.target.value)}
          className="w-full accent-[#D4AF37]" />
      </div>
      <Group label="Rating" param="min_rating" value={minRating} options={[{ v: "4.5", l: "4.5 & up" }, { v: "4.7", l: "4.7 & up" }, { v: "4.9", l: "4.9 & up" }]} />
      <div>
        <p className="eyebrow mb-3">Availability</p>
        <label className="flex items-center gap-2 text-sm text-[#FAFAF7]/60 cursor-pointer">
          <input type="checkbox" data-testid="filter-in-stock" checked={!!inStock} onChange={(e) => setParam("in_stock", e.target.checked ? "1" : "")} className="accent-[#D4AF37]" />
          In stock only
        </label>
      </div>
    </div>
  );

  return (
    <div className="bg-[#0B0B0C] min-h-screen pt-28 sm:pt-32">
      <SEO title={title} path="/shop" description={`Shop ${title} at Queen MJ Maison Beaute — authentic luxury perfumes and home fragrance with complimentary shipping over $150.`} />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <div className="mb-8">
          <p className="eyebrow mb-3">QUEEN MJ MAISON BEAUTE</p>
          <h1 className="font-serif-lux text-4xl sm:text-5xl font-light text-[#FAFAF7]">{title}</h1>
        </div>

        <div className="flex items-center justify-between mb-8 gap-4">
          <button data-testid="mobile-filter-toggle" onClick={() => setShowFilters(true)} className="lg:hidden flex items-center gap-2 gold-border rounded-full px-4 py-2 text-sm text-[#FAFAF7]">
            <SlidersHorizontal size={15} /> Filters {activeCount > 0 && `(${activeCount})`}
          </button>
          <p className="text-sm text-[#FAFAF7]/50 hidden lg:block">{products.length} fragrances</p>
          <select data-testid="sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)}
            className="bg-[#141416] gold-border rounded-full px-4 py-2 text-sm text-[#FAFAF7] outline-none cursor-pointer">
            {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
          <aside className="hidden lg:block"><FilterPanel /></aside>

          <div>
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
                {Array(6).fill(0).map((_, i) => <div key={i} className="aspect-[3/4] bg-[#141416] rounded-lg animate-pulse" />)}
              </div>
            ) : products.length === 0 ? (
              <p className="text-[#FAFAF7]/50 py-20 text-center font-serif-lux text-2xl">No fragrances match your filters.</p>
            ) : isDefaultView ? (
              <div className="space-y-16" data-testid="product-grid">
                {groups.map((grp) => (
                  <div key={grp.key} data-testid={`category-section-${grp.key.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div className="flex items-end justify-between mb-6 pb-3 border-b border-[rgba(212,175,55,0.15)]">
                      <div>
                        <h2 className="font-serif-lux text-3xl text-[#FAFAF7]">{grp.label}</h2>
                        <p className="text-xs text-[#FAFAF7]/45 mt-1">{grp.desc} · {grp.items.length} item{grp.items.length > 1 ? "s" : ""}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
                      {grp.items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8" data-testid="product-grid">
                {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-[#0B0B0C] border-l border-[rgba(212,175,55,0.25)] p-6 overflow-y-auto">
            <div className="flex justify-between items-center mb-8">
              <span className="font-serif-lux text-xl text-[#FAFAF7]">Filters</span>
              <button data-testid="mobile-filter-close" onClick={() => setShowFilters(false)}><X className="text-[#FAFAF7]" /></button>
            </div>
            <FilterPanel />
          </div>
        </div>
      )}
    </div>
  );
}
